import React, { useState } from 'react';
import TimeFrameModal from './TimeFrameModal';
import './PerDayTimeFrameEditor.css';

const PerDayTimeFrameEditor = ({
    selectedDays = [],
    dayTimeframes = {},
    onChange,
    errors = {},
    globalStartTime = '',
    globalEndTime = ''
}) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
    const [selectedDayForModal, setSelectedDayForModal] = useState(null);
    const [editingRanges, setEditingRanges] = useState([]);

    const normalizeSelected = (val) => {
        if (Array.isArray(val)) return val;
        if (typeof val === 'string') return val.split(',').map(d => d.trim()).filter(Boolean);
        return [];
    };

    const selectedArray = normalizeSelected(selectedDays);

    const formatTime = (time) => {
        if (!time) return '';
        // Return time in 24-hour format (HH:MM)
        return time;
    };

    const openModal = (day) => {
        setSelectedDayForModal(day);
        const existingRanges = dayTimeframes[day] || [];

        if (existingRanges.length > 0) {
            // Edit mode - has existing time frames
            setModalMode('edit');
            setEditingRanges(existingRanges);
        } else {
            // Add mode - no existing time frames
            setModalMode('add');
            setEditingRanges([]);
        }

        setModalOpen(true);
    };

    const openAddModal = (day) => {
        openModal(day);
    };

    const openEditModal = (day) => {
        openModal(day);
    };

    const handleModalSave = (timeRanges, applyToDays) => {
        const updatedTimeframes = { ...dayTimeframes };

        applyToDays.forEach(day => {
            if (modalMode === 'edit') {
                // Replace existing ranges
                updatedTimeframes[day] = timeRanges;
            } else {
                // Add new ranges to existing ones
                const existing = updatedTimeframes[day] || [];
                updatedTimeframes[day] = [...existing, ...timeRanges];
            }
        });

        onChange(updatedTimeframes);
    };

    const removeTimeRange = (day, index) => {
        const currentRanges = dayTimeframes[day] || [];
        const updatedRanges = currentRanges.filter((_, i) => i !== index);
        const updatedTimeframes = {
            ...dayTimeframes,
            [day]: updatedRanges
        };
        onChange(updatedTimeframes);
    };

    const getDayErrors = (day) => {
        return errors[day] || [];
    };

    return (
        <div className="per-day-timeframe-editor">
            {/* Global Time Reference */}
            {globalStartTime && globalEndTime && (
                <div className="global-time-reference">
                    <i className="fa fa-clock-o"></i>
                    <span className="global-time-label">Global Campaign Time:</span>
                    <span className="global-time-value">
                        {formatTime(globalStartTime)} - {formatTime(globalEndTime)}
                    </span>
                </div>
            )}

            {selectedArray.length === 0 ? (
                <div className="alert alert-warning">
                    <i className="fa fa-exclamation-triangle"></i>
                    Please select days of the week above to configure time frames.
                </div>
            ) : (
                <div className="timeframe-table">
                    <div className="timeframe-table-header">
                        <div className="col-weekday">Days Of Week</div>
                        <div className="col-timeframes">Time Frames</div>
                        <div className="col-actions">Actions</div>
                    </div>

                    {selectedArray.map((day) => {
                        const timeRanges = dayTimeframes[day] || [];
                        const dayErrors = getDayErrors(day);

                        return (
                            <div key={day} className="timeframe-table-row">
                                <div className="col-weekday">
                                    <span className="weekday-name">{day}</span>
                                </div>

                                <div className="col-timeframes">
                                    <div className="timeframe-badges">
                                        {timeRanges.map((range, index) => (
                                            <div key={index} className="timeframe-badge">
                                                <span className="badge-time">
                                                    {range.start_time && range.end_time ? (
                                                        <>
                                                            {formatTime(range.start_time)} - {formatTime(range.end_time)}
                                                        </>
                                                    ) : (
                                                        <span className="incomplete-badge">Incomplete</span>
                                                    )}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="badge-delete"
                                                    onClick={() => removeTimeRange(day, index)}
                                                    title="Remove"
                                                >
                                                    <i className="fa fa-times"></i>
                                                </button>
                                            </div>
                                        ))}
                                    </div>

                                    {dayErrors.length > 0 && (
                                        <div className="day-errors">
                                            {dayErrors.map((err, i) => (
                                                <div key={i} className="error-message">
                                                    <i className="fa fa-exclamation-circle"></i>
                                                    {err.message}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="col-actions">
                                    <button
                                        type="button"
                                        className={timeRanges.length > 0 ? "btn-edit-timeframes" : "btn-add-timeframe"}
                                        onClick={() => openModal(day)}
                                    >
                                        <i className={timeRanges.length > 0 ? "fa fa-edit" : "fa fa-plus"}></i>
                                        {timeRanges.length > 0 ? 'Edit Time Frame' : 'Add Time Frame'}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Time Frame Modal */}
            <TimeFrameModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                onSave={handleModalSave}
                selectedDay={selectedDayForModal}
                existingRanges={editingRanges}
                allSelectedDays={selectedArray}
                globalStartTime={globalStartTime}
                globalEndTime={globalEndTime}
                editMode={modalMode === 'edit'}
            />
        </div>
    );
};

export default PerDayTimeFrameEditor;
